import { chargerActions } from '../slices/charger-slice';
import { PrepareApiProcedure } from '../../components/common/utils/prepare-api-procedure';
import {
  ApiRequest,
  fetchDataFromAPI,
  fetchDataFromAPITyped,
  isSuccessful,
} from '../../services/api-requests';
import { ChargerAttributes } from '../../models/charger-model';
import { notificationService } from '../../services/notification-service';
import {
  getCurrentUtcSeconds,
  getPastUtcSeconds,
} from '../../components/common/utils/date-time-conversions';
import * as Location from 'expo-location';
import { AppDispatch, RootState } from '..';

/**
 * Represents a schedule for an EVSE charger.
 */
export interface ChargerSchedule {
  scheduleId: string;
  startTime: string; // ISO 8601 format
  endTime: string; // ISO 8601 format
  device_id?: string;
  chargerId?: string;
  orderId?: string;
  max_kwh?: number;
  createdAt?: string;
  updatedAt?: string;
  message?: string;
  // Present on error responses from charger/schedule/set. When the backend
  // rejects the request it returns { message, ... } describing why.
  // `immediate` is a non-error signal that an OCPP charger can start charging
  // now, so the caller should skip scheduling and proceed straight to checkout.
  immediate?: boolean;
  ocpp_status?: string;
  conflicts?: unknown;
  error?: string;
}

export interface SendSchedule {
  start_time: string;
  end_time: string;
  chargerId: string;
  orderId: string;
  max_kwh?: number | null;
  // "Charge now": the user did not confirm a schedule. Emporia (DERMS)
  // chargers can only be driven by a schedule, so the backend still creates
  // one, but anchors its start to the server clock instead of trusting
  // `start_time` (which can be stale client state).
  immediate?: boolean;
}

/**
 * Response for DELETE schedules.
 */
interface DeleteSchedulesResponse {
  success: string[]; // list of schedule_ids that were deleted
  error: Array<{
    schedule_id: string;
    message: string;
    error_code: string;
  }>;
}

/**
 * Retrieves schedules for one or more devices.
 *
 * @param deviceIds - A single device ID or an array of device IDs.
 */
export const getSchedule = async (
  deviceIds: string | string[]
): Promise<ChargerSchedule | null> => {
  try {
    // Convert array to comma-separated string if needed.
    const deviceIdsParam = Array.isArray(deviceIds)
      ? deviceIds.join(',')
      : deviceIds;

    const apiQuery: ApiRequest = {
      route: `charger/schedule/get/${deviceIdsParam}`,
      request: {
        method: 'GET',
        entity: 'schedule',
        body: undefined,
      },
    };

    const response = await fetchDataFromAPITyped<ChargerSchedule>(apiQuery);
    if (!response || !isSuccessful(response.status)) {
      notificationService.sendNotification('error', 'Failed to get schedule');
      return null;
    } else {
      return response.payload;
    }
  } catch (error) {
    console.error('Error while fetching schedule', error);
    notificationService.sendNotification(
      'error',
      `Error while fetching schedule: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return null;
  }
};

/**
 * Creates or updates a schedule.
 *
 * @param schedule - A schedule object to be created or updated.
 */
export const setSchedule = async (
  schedule: SendSchedule
): Promise<ChargerSchedule | ChargerSchedule[] | null> => {
  try {
    const apiQuery: ApiRequest = {
      route: `charger/schedule/set`,
      request: {
        method: 'POST',
        entity: 'schedule',
        body: schedule,
      },
    };

    const response = await fetchDataFromAPITyped<
      ChargerSchedule | ChargerSchedule[]
    >(apiQuery);
    if (!response || !isSuccessful(response.status)) {
      const errorPayload = (response?.payload ?? null) as ChargerSchedule | null;
      const backendMessage = errorPayload?.message;

      if (errorPayload?.immediate) {
        // Not a failure: the OCPP charger can start charging now, so the
        // caller should skip scheduling and proceed to checkout.
        notificationService.sendNotification(
          'info',
          backendMessage ?? 'This charger can start charging immediately.'
        );
      } else {
        // Surface the specific reason the backend rejected the schedule
        // (e.g. charger in use, overlapping window, scheduling not enabled)
        // instead of a generic message.
        notificationService.sendNotification(
          'error',
          backendMessage ?? 'Failed to set schedule'
        );
      }
      return errorPayload;
    } else {
      notificationService.sendNotification('success', 'Success!');
      return response.payload;
    }
  } catch (error) {
    console.error('Error while setting schedule', error);
    notificationService.sendNotification(
      'error',
      `Error while setting schedule: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return null;
  }
};

export interface SchedulingPreferenceResponse {
  chargerId: string;
  enabled: boolean;
  can_schedule?: boolean;
  message?: string;
}

/**
 * Update the host's per-charger "offer scheduled sessions" preference.
 * OCPP 2.0.1 only — callers should gate on supportsOcpp201(charger) before
 * exposing UI for this. The backend ignores the call (or 404s) for
 * Emporia / OCPP 1.6 chargers.
 */
export const setChargerSchedulingPreference = async (
  chargerId: string,
  enabled: boolean
): Promise<SchedulingPreferenceResponse | null> => {
  try {
    const apiQuery: ApiRequest = {
      route: 'charger/scheduling/preference',
      request: {
        method: 'POST',
        entity: 'charger',
        body: { chargerId, enabled },
      },
    };

    const response =
      await fetchDataFromAPITyped<SchedulingPreferenceResponse>(apiQuery);
    if (!response || !isSuccessful(response.status)) {
      const msg =
        (response?.payload as any)?.message ||
        'Failed to update scheduling preference';
      notificationService.sendNotification('error', msg);
      return null;
    }
    notificationService.sendNotification(
      'success',
      enabled
        ? 'Scheduled sessions enabled for this charger'
        : 'Scheduled sessions disabled for this charger'
    );
    return response.payload;
  } catch (error) {
    console.error('Error while updating scheduling preference', error);
    notificationService.sendNotification(
      'error',
      `Error updating scheduling preference: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return null;
  }
};

/**
 * Deletes one or more schedules.
 *
 * @param scheduleIds - A single schedule ID or an array of schedule IDs to delete.
 * @param options.silent - Suppress user notifications. Used for internal
 * cleanup (e.g. removing a just-created schedule after checkout fails) where
 * a "Successfully removed schedule!" toast would only confuse the user.
 */
export const deleteSchedule = async (
  scheduleIds: string | string[],
  options?: { silent?: boolean }
): Promise<boolean> => {
  try {
    // Convert array to comma-separated string if needed.
    const scheduleIdsParam = Array.isArray(scheduleIds)
      ? scheduleIds.join(',')
      : scheduleIds;
    const apiQuery: ApiRequest = {
      route: `charger/schedule/delete/${scheduleIdsParam}`,
      request: {
        method: 'DELETE',
        entity: 'schedule',
        body: undefined,
      },
    };

    const response = await fetchDataFromAPITyped<DeleteSchedulesResponse>(
      apiQuery
    );
    if (!response || !isSuccessful(response.status)) {
      if (!options?.silent) {
        notificationService.sendNotification(
          'error',
          'Failed to delete schedule'
        );
      }
      return false;
    } else {
      if (!options?.silent) {
        notificationService.sendNotification(
          'success',
          'Successfully removed schedule!'
        );
      }
      return true;
    }
  } catch (error) {
    console.error('Error while deleting schedule', error);
    if (!options?.silent) {
      notificationService.sendNotification(
        'error',
        `Error while deleting schedule: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
    return false;
  }
};
